import { useState, type ReactElement } from 'react';
import { Ban, Loader2, ShieldCheck } from 'lucide-react';
import type { OrganizationId } from '@kirin/api-client/contracts/organizations';
import { Button } from '@/components/ui/button';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  usePackageBlocked,
  usePackageListActions,
  type PackageIdentity,
} from '@/hooks/api/usePackageListActions';
import { cn } from '@/lib/utils';
import {
  PACKAGE_LISTS_LOAD_FAILED_TOOLTIP,
  PACKAGE_MASKED_TOOLTIP,
} from '@/components/alertActionTooltipCopy';

interface AlertPackageBlockButtonProps {
  /** Undefined for the personal (account) scope. */
  organizationId: OrganizationId | undefined;
  /** Null when the alert's package identity is masked; `disabledReason` must then be set. */
  pkg: PackageIdentity | null;
  className?: string;
  /**
   * When set, the button is shown disabled with this explanation (e.g. a
   * support user's read-only access).
   */
  disabledReason?: string;
}

/**
 * KIR-4443: the alert action for a package — "Block package", or "Unblock
 * package" when it is already blocked. Packages have no Allow list; on the
 * Packages page, Allowed means "not blocked".
 *
 * Unblock removes the package from both lists, exactly like switching its
 * Packages row to Allowed, so detection keeps running for it.
 */
export function AlertPackageBlockButton({
  organizationId,
  pkg,
  className,
  disabledReason,
}: AlertPackageBlockButtonProps): ReactElement {
  const actions = usePackageListActions(organizationId, 'alert');
  const { blocked, isError } = usePackageBlocked(organizationId, pkg);
  const [confirmingBlock, setConfirmingBlock] = useState(false);

  const label = blocked ? 'Unblock package' : 'Block package';
  const Icon = blocked ? ShieldCheck : Ban;
  // Without both lists the button cannot know which action it offers; say so
  // rather than spin.
  const unavailableReason =
    disabledReason ??
    (pkg == null
      ? PACKAGE_MASKED_TOOLTIP
      : isError && blocked === undefined
        ? PACKAGE_LISTS_LOAD_FAILED_TOOLTIP
        : undefined);

  if (unavailableReason !== undefined || pkg == null) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} className="inline-flex">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={cn(className, 'pointer-events-none opacity-50')}
              aria-disabled="true"
              aria-label={`${label} (unavailable)`}
              onClick={event => event.preventDefault()}
            >
              <Icon className="mr-1 size-4" />
              <span>{label}</span>
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>{unavailableReason}</TooltipContent>
      </Tooltip>
    );
  }

  // Until both lists load, the button cannot know which action it offers.
  const busy = blocked === undefined || actions.isPending(pkg);

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className={className}
        disabled={busy}
        aria-busy={busy}
        onClick={() => {
          if (blocked) void actions.clear(pkg);
          else setConfirmingBlock(true);
        }}
      >
        {busy ? (
          <Loader2 className="mr-1 size-4 animate-spin" />
        ) : (
          <Icon className="mr-1 size-4" />
        )}
        <span>{label}</span>
      </Button>
      <ConfirmationDialog
        isOpen={confirmingBlock}
        onClose={() => setConfirmingBlock(false)}
        onConfirm={() => {
          setConfirmingBlock(false);
          void actions.block(pkg);
        }}
        title={`Block the "${pkg.name}" package?`}
        description={
          `${
            actions.scope === 'organization'
              ? 'This applies to everyone in this organization. '
              : ''
          }Applies to every version, from every package manager that ` +
          `installs from ${pkg.ecosystem}. Coding agents are denied the ` +
          'install before it runs.'
        }
        confirmLabel="Block"
      />
    </>
  );
}
